import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Marquee, MarqueeItem } from "../ui/Marquee/Marquee";
import { ImageSizes } from "../../utils/imageTypes";
import { cdnImages, GalleryCarouselImage } from "../../utils/imageMap.cdn";
import "./Gallery.css";

type ImageSize = keyof ImageSizes;
type LoadingType = "eager" | "lazy";

const GalleryComponent: React.FC = () => {
  const [logoWidth, setLogoWidth] = useState(550);
  const [speed, setSpeed] = useState(50);
  const [gap, setGap] = useState(20);
  const [currentSize, setCurrentSize] = useState<ImageSize>("xl");

  const handleResize = useCallback(() => {
    if (window.innerWidth <= 480) {
      setLogoWidth(200);
      setGap(10);
      setSpeed(30);
      setCurrentSize("sm");
    } else if (window.innerWidth <= 768) {
      setLogoWidth(300);
      setGap(15);
      setSpeed(40);
      setCurrentSize("md");
    } else if (window.innerWidth <= 1024) {
      setLogoWidth(400);
      setGap(20);
      setSpeed(45);
      setCurrentSize("lg");
    } else {
      setLogoWidth(550);
      setGap(20);
      setSpeed(50);
      setCurrentSize("xl");
    }
  }, []);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  const galleryImages = useMemo<MarqueeItem[]>(() => {
    const fileNumbers: GalleryCarouselImage[] = [
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      "10",
    ];
    return fileNumbers.map((num, i) => {
      const image = cdnImages.galleryCarousel[num];
      const loading: LoadingType = i < 3 ? "eager" : "lazy";

      // Create srcSet for responsive images
      const sizes = {
        sm: { width: 200, url: image.sm },
        md: { width: 300, url: image.md },
        lg: { width: 400, url: image.lg },
        xl: { width: 550, url: image.xl },
      };

      return {
        id: num,
        src: image[currentSize],
        srcSet: Object.values(sizes)
          .map(({ width, url }) => `${url} ${width}w`)
          .join(", "),
        sizes: `
        (max-width: 480px) 200px,
        (max-width: 768px) 300px,
        (max-width: 1024px) 400px,
        550px
        `.trim(),
        alt: `Gallery Image ${num}`,
        loading,
      };
    });
  }, [currentSize]);

  return (
    <div className={`image-carousel-container md:justify-end justify-center brightness-50`}>
      <Marquee
        items={galleryImages}
        speed={speed}
        direction="left"
        logoWidth={logoWidth}
        gap={gap}
        itemClassName="carousel-item"
        className="perspective-container md:block hidden"
      />

      <div className="md:hidden h-full brightness-50 w-fit relative flex flex-col items-center justify-center gap-4">
        <Marquee
          items={galleryImages}
          speed={200}
          direction="left"
          logoWidth={logoWidth}
          gap={gap}
          itemClassName="!h-[40vh] !w-[60vw] !px-0 rounded-[10px] overflow-hidden"
          className="h-[42vh] w-[130vw] -rotate-12"
          imgClassName="h-fit !p-0 !object-cover"
        />

        <Marquee
          items={galleryImages}
          speed={200}
          direction="right"
          logoWidth={logoWidth}
          gap={gap}
          itemClassName="!h-[40vh] !w-[60vw] !px-0 rounded-[10px] overflow-hidden"
          className="h-[42vh] w-[130vw] -rotate-12"
          imgClassName="h-fit !p-0 !object-cover"
        />
      </div>
    </div>
  );
};

export const Gallery = React.memo(GalleryComponent);
